import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Database, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login({ showPassword = false }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginSuccess } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      loginSuccess(data);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const handleForgot = async () => {
    if (!email) { toast.error("Enter your email above first"); return; }
    try { await api.post("/auth/forgot-password", { email }); } catch (err) { console.debug("forgot-password request failed (ignored):", err); }
    toast.success("If that account exists, a reset link has been sent");
  };

  const microsoftLogin = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/auth/microsoft/login`;
    try { (window.top || window).location.href = url; } catch (e) { window.location.href = url; }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/google/callback";
    const url = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    try { (window.top || window).location.href = url; } catch (e) { window.location.href = url; }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#002FA7] text-white p-12">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-white/15 flex items-center justify-center rounded-sm">
            <Database className="w-5 h-5" strokeWidth={2} />
          </div>
          <span className="font-heading font-extrabold text-lg tracking-tight">ClientCases</span>
        </div>
        <div>
          <h1 className="font-heading text-4xl xl:text-5xl font-extrabold tracking-tighter leading-tight">
            Every client.<br/>One source of truth.
          </h1>
          <p className="mt-5 text-blue-100 text-base max-w-md leading-relaxed">
            Client Case Tracking &amp; Integration Database.
          </p>
        </div>
        <p className="text-blue-200 text-xs">© 2026 ClientCases</p>
      </div>

      <div className="flex items-center justify-center p-6 bg-[#F9FAFB]">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form" autoComplete="off">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight text-gray-900">Sign in</h2>
          <p className="text-gray-500 text-sm mt-1.5 mb-7">
            {showPassword ? "Administrator sign-in." : "Access your client database with your Microsoft 365 or Google account."}
          </p>

          {showPassword && (
            <>
              {error && (
                <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm px-3 py-2" data-testid="login-error">
                  {error}
                </div>
              )}

              <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
              <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" data-lpignore="true" data-form-type="other"
                className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent mb-4" />

              <label className="block text-sm font-semibold text-gray-700 mb-1">Password</label>
              <input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                autoComplete="new-password" autoCorrect="off" autoCapitalize="off" spellCheck="false" data-lpignore="true" data-form-type="other"
                className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent mb-2" />

              <div className="flex justify-end mb-5">
                <button type="button" onClick={handleForgot} data-testid="forgot-password-link"
                  className="text-xs font-semibold text-[#002FA7] hover:underline">Forgot password?</button>
              </div>

              <button type="submit" disabled={loading} data-testid="login-submit-button"
                className="w-full bg-[#002FA7] hover:bg-[#002480] text-white py-2.5 rounded-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
              </button>

              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400 font-medium">or</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
            </>
          )}

          <button type="button" onClick={microsoftLogin} data-testid="microsoft-login-button"
            className="w-full bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 py-2.5 rounded-sm font-semibold transition-colors flex items-center justify-center gap-2.5">
            <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true"><path fill="#f25022" d="M1 1h10v10H1z"/><path fill="#7fba00" d="M12 1h10v10H12z"/><path fill="#00a4ef" d="M1 12h10v10H1z"/><path fill="#ffb900" d="M12 12h10v10H12z"/></svg>
            Sign in with Microsoft
          </button>

          <button type="button" onClick={googleLogin} data-testid="google-login-button"
            className="w-full mt-3 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 py-2.5 rounded-sm font-semibold transition-colors flex items-center justify-center gap-2.5">
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Sign in with Google
          </button>

          <p className="text-sm text-gray-400 mt-6 text-center">
            Need an account? Ask your administrator to create one.
          </p>
        </form>
      </div>
    </div>
  );
}
